import { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Box } from '@mui/material'
import MoreOptionsForBookedSlotStrip from 'src/clinic/components/ClinicManageBookingsPage/slotItem/components/MoreOptionsForBookedSlotStrip'
import { selectAppointmentCalendarSettings } from 'src/clinic/reducers/prescription/prescriptionReducer'
import {
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_THERAPY_SESSION,
  EVENT_TYPE_SCREENING,
  VISIT_EVENT_TYPES,
  BOOKING_STATUSES,
  BOOKING_PAYMENT_STATUSES,
  SESSION_PLATFORM_ONLINE,
} from 'src/consumer/constants'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import { useScreenSize } from 'src/customHooks/customHooks'
import { EDVCSlotColoredItem } from '../ClinicManageBookingsPageStyles'
import StepsCheckUI from '../screening/StepsCheckUI'
import EventTypeAndStepsStrip from './component/EventTypeAndStepsStrip'
import InTherapyButton from './component/InTherapyButton'
import ActionButtons from './components/ActionButtons'
import BookedSlotDialogs from './components/BookedSlotDialogs'
import UnifiedActionsDrawer from './components/UnifiedActionsDrawer'
import BookedSlotHeader from './components/BookedSlotHeader'
import PatientInfo from './components/PatientInfo'
import VisitCornerBadge from './components/VisitCornerBadge'
import { useBookedSlotStyles } from './hooks/useBookedSlotStyles'
import { useMultipleDialogs } from './hooks/useDialogState'
import { usePrescriptionDialogs } from './hooks/usePrescriptionDialogs'
import { usePrescriptionDownload } from './hooks/usePrescriptionDownload'
import { calculateCreatedTimeFrom, calculateCreatedTimeTo } from './utils/timeCalculations'
import { getEmrApiData } from 'src/shared/utils/calendarReportApiHelper'

const BookedSlotItemContent = ({
  slotBlocked: isSlotBookedInOtherClinic,
  bookedSlot,
  forMentor,
  minimalUI,
  handleNewTherapyRequest,
  therapyBookingDataForThisEvent,
  setClickedBookedSlotData,
  setEditPatientDialogOpen,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  hidePreviousTherapyReportsButton,
  openReceiptDialog,
  setEventDataForReceiptPrinting,
  canPersonPrintPrescription,
  setPendingActionsDialogOpen,
  pendingActionsForThisBookedEvent,
  onClickItem,
  minutesPassedSinceStartOfTimeBlock,
  eachSlotDurationInMins,
  heightOfEachSlot,
  durationOfBookedSlotInMins,
  itemContainerStyles = {},
  slotContainerRef,
  refreshBookedSlots,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  clinicBrandData,
  clinicLocationData,
  clinicAssociationsData,
  clinicAssociations,
  mentorProfileData,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages = false,
  includeReportRelatedData,
  showPrescriptionPdfDownloadButtons = false,
  sendPrescriptionPdfApiSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
  showPatientHistoryInUploadPrescriptionWidget,
  patientHistoryGetApiSignature,
  forceShowJoiningDetailsActionButton,
  hideRescheduleDialog = true,
  showSendingLinkButtonsToSupport = false,
  showViewReportsButtonInActionButtons = false,
  openReportViewDrawer,
  onOpenMainDrawer, // New prop to open main drawer
  vitalsFormGetApiSignature,
  bookedSlotsInDay,
  onGoToEvent,
  overlapIndex = 0,
  overlapTotal = 1,
}) => {
  const innerFlexContainerRef = useRef(null)
  const menuRef = useRef(null)
  const stripMeasureRef = useRef(null)
  const apptCalendarSettingsData = useSelector(selectAppointmentCalendarSettings)
  const apptCalendarSettings = apptCalendarSettingsData?.data
  const { width } = useScreenSize()
  const isMobile = width < 768

  // When strip is too narrow (e.g. many overlapping slots), show compact view and open drawer on click
  const NARROW_STRIP_THRESHOLD_PX = 120
  const [isNarrowStrip, setIsNarrowStrip] = useState(false)
  useEffect(() => {
    const el = stripMeasureRef.current
    if (!el) return
    const check = () => {
      const w = el.offsetWidth || 0
      setIsNarrowStrip(w > 0 && w < NARROW_STRIP_THRESHOLD_PX)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [overlapTotal, overlapIndex])

  // Initialize dialog states
  const dialogs = useMultipleDialogs([
    'sendTherapy',
    'paymentLinks',
    'joiningDetails',
    'bookedEventLogs',
    'uploadPrescriptionImages',
    'sendAssessment',
    'patientBooking',
    'emrImages',
    'reschedule',
  ])

  // Unified drawer state
  const [unifiedDrawerOpen, setUnifiedDrawerOpen] = useState(false)
  const [unifiedDrawerInitialTab, setUnifiedDrawerInitialTab] = useState('joiningDetails')
  const [useUnifiedDrawer, setUseUnifiedDrawer] = useState(forMentor) // Use unified drawer for mentor flow

  // Helper to open unified drawer with specific tab
  const openUnifiedDrawer = (tab) => {
    setUnifiedDrawerInitialTab(tab)
    setUnifiedDrawerOpen(true)
  }

  // Check if we're in support portal by checking the pathname
  const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
  
  // For mentors and support portal, show slots from other clinics as normal with clinic indicator
  const showAsNormalForMentorOrSupport = (forMentor || isSupportPortal) && isSlotBookedInOtherClinic
  // Show clinic indicator for blocked slots (other clinics) OR if we have clinic location data with nickname
  const showClinicIndicator = (isSlotBookedInOtherClinic && (forMentor || isSupportPortal)) || 
                              (clinicLocationData?.nickName || clinicLocationData?.nickname)

  // Calculate styles once
  const styles = useBookedSlotStyles(bookedSlot, isSlotBookedInOtherClinic, showAsNormalForMentorOrSupport)

  // Custom hooks for prescription functionality
  const prescriptionDownload = usePrescriptionDownload({
    bookedSlot,
    sendPrescriptionPdfApiSignature,
    refreshBookedSlots,
  })

  const prescriptionDialogs = usePrescriptionDialogs(prescriptionDownload)

  // Calculate prescription upload button visibility
  const showPrescriptionUploadButton = (() => {
    if (bookedSlot?.eventType !== EVENT_TYPE_CONSULTATION) return false

    if (forMentor) {
      return getAllRolesInBoolean().isDoctor || getAllRolesInBoolean().isClinicSuccessManager
    }
    return clinicBrandData?.canAssistantUploadPrescription
  })()

  // Calculate EMR images API signatures and dialog title based on event type
  // Using centralized helper function for portal/role-aware API selection
  const emrApiData = getEmrApiData(bookedSlot?.eventType)

  // Event handlers
  const openMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    menuRef.current.openMenuOnAnchor(e)
  }

  const openSendTherapyPopover = () => {
    if (useUnifiedDrawer && forMentor) {
      // Open unified drawer with referral tab for mentors
      openUnifiedDrawer('referPatient')
    } else {
      dialogs.sendTherapy.open()
    }
  }

  // Wrapper handlers for unified drawer
  const handleOpenJoiningDetails = () => {
    if (useUnifiedDrawer && forMentor) {
      openUnifiedDrawer('joiningDetails')
    } else {
      dialogs.joiningDetails.open()
    }
  }

  const handleOpenUploadPrescription = () => {
    if (useUnifiedDrawer && forMentor) {
      openUnifiedDrawer('uploadPrescription')
    } else {
      dialogs.uploadPrescriptionImages.open()
    }
  }

  const handleOpenAddVitals = () => {
    if (useUnifiedDrawer && forMentor) {
      openUnifiedDrawer('addVitals')
    } else {
      setAddVitalsDialogOpen(true)
      setClickedBookedSlotData(bookedSlot)
    }
  }

  const handleToggleRecording = async () => {
    const loadingToast = toast.loading('Updating Recording Status...')
    const apiUrl = !bookedSlot?.recordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-recordable'
      : '/api/secure/cc/support/events/mark-event-as-not-recordable'

    const resp = await putSPDataWithParams(apiUrl, null, {
      'booked-event-id': bookedSlot?.id,
    })

    if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      refreshBookedSlots()
      toast.update(loadingToast, {
        render: 'Recording Status Updated',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
    } else {
      toast.update(loadingToast, {
        render: 'Failed to update recording status',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  return (
    <>
      <EDVCSlotColoredItem
        ref={(el) => {
          stripMeasureRef.current = el
          if (typeof slotContainerRef === 'function') slotContainerRef(el)
          else if (slotContainerRef) slotContainerRef.current = el
        }}
        onClick={(e) => {
          if (isNarrowStrip && onOpenMainDrawer) {
            e.preventDefault()
            e.stopPropagation()
            onOpenMainDrawer(bookedSlot)
          } else {
            onClickItem?.(e)
          }
        }}
        emergencyEvent={bookedSlot?.visitEventType === VISIT_EVENT_TYPES.emergency}
        selected={false}
        booked={true}
        completed={bookedSlot?.markedCompleted}
        blocked={isSlotBookedInOtherClinic && !showAsNormalForMentorOrSupport}
        isExternalClinic={isSlotBookedInOtherClinic && showAsNormalForMentorOrSupport}
        overlapIndex={overlapIndex}
        overlapTotal={overlapTotal}
        style={{ ...itemContainerStyles, padding: '0px' }}
        fromTopInPx={
          minutesPassedSinceStartOfTimeBlock &&
          eachSlotDurationInMins &&
          heightOfEachSlot &&
          (minutesPassedSinceStartOfTimeBlock / eachSlotDurationInMins) * heightOfEachSlot
        }
        heightInPx={
          durationOfBookedSlotInMins &&
          eachSlotDurationInMins &&
          heightOfEachSlot &&
          (durationOfBookedSlotInMins / eachSlotDurationInMins) * heightOfEachSlot
        }
        stripColor={styles.background}
        stripBorderColor={styles.borderColor}
        stripTextColor={styles.fontColor}
      >
        {isNarrowStrip ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minWidth: 0,
              overflow: 'hidden',
              padding: '0 8px',
              fontSize: '11px',
              fontWeight: 600,
              color: 'inherit',
            }}
          >
            #{bookedSlot?.appointmentNumber ?? '–'} · {(bookedSlot?.patientInfo?.name || 'Patient').trim().split(/\s+/)[0] || '…'}
          </Box>
        ) : (
        <Box
          ref={innerFlexContainerRef}
          sx={{ 
            position: 'relative', 
            padding: '0px 10px 0px 0px',
            minWidth: 0, // Allow flex children to shrink below content size
            overflow: 'hidden', // Prevent overflow
            display: 'flex',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          {/* Visit corner badge */}
          <VisitCornerBadge
            bookedSlot={bookedSlot}
            apptCalendarSettings={apptCalendarSettings}
            styles={styles}
          />

          {isSlotBookedInOtherClinic && !showAsNormalForMentorOrSupport ? (
            <>
              <Box
                sx={{ 
                  display: 'flex',
                  height: '100%',
                  alignItems: 'center',
                  flex: '1 1 auto',
                  minWidth: 0, // Allow truncation
                  overflow: 'hidden', // Prevent overflow
                  marginRight: '8px', // Space before action buttons
                }}
              >
                {/* Left strip - show BLOCKED or appointment number */}
                <BookedSlotHeader
                  bookedSlot={bookedSlot}
                  apptCalendarSettings={apptCalendarSettings}
                  styles={styles}
                  forMentor={forMentor}
                  hidePreviousTherapyReportsButton={hidePreviousTherapyReportsButton}
                />
                
                <Box
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    flex: '1 1 auto',
                    minWidth: 0, // Allow truncation
                    overflow: 'hidden', // Prevent overflow
                  }}
                >
                  {/* Patient information */}
                  <PatientInfo
                    bookedSlot={bookedSlot}
                    apptCalendarSettings={apptCalendarSettings}
                    forMentor={forMentor}
                    styles={styles}
                    clinicAssociationsData={clinicAssociationsData}
                    showClinicIndicator={showClinicIndicator}
                    openPaymentLinksDialog={dialogs.paymentLinks.open}
                    isBlockedSlot={true}
                    isSlotBookedInOtherClinic={isSlotBookedInOtherClinic}
                    clinicAssociations={clinicAssociations}
                  />
                </Box>
              </Box>
              {/* Action buttons - ensure plus icon is visible */}
              <ActionButtons
                bookedSlot={bookedSlot}
                forMentor={forMentor}
                minimalUI={false} // Always show plus icon for blocked slots
                therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
                handleNewTherapyRequest={handleNewTherapyRequest}
                canPersonPrintPrescription={canPersonPrintPrescription}
                showPrescriptionUploadButton={showPrescriptionUploadButton}
                openReceiptDialog={openReceiptDialog}
                setEventDataForReceiptPrinting={setEventDataForReceiptPrinting}
                openUploadPrescriptionImagesDialog={dialogs.uploadPrescriptionImages.open}
                openPaymentLinksDialog={dialogs.paymentLinks.open}
                openEmrImagesDialog={dialogs.emrImages.open}
                openMenu={openMenu}
                onOpenMainDrawer={onOpenMainDrawer}
              />
            </>
          ) : (
            <>
              <Box
                sx={{ 
                  display: 'flex',
                  height: '100%',
                  alignItems: 'center',
                  flex: '1 1 auto',
                  minWidth: 0, // Allow truncation
                  overflow: 'hidden', // Prevent overflow
                  marginRight: '8px', // Space before action buttons
                }}
              >
                {/* Left strip with appointment number */}
                <BookedSlotHeader
                  bookedSlot={bookedSlot}
                  apptCalendarSettings={apptCalendarSettings}
                  styles={styles}
                  forMentor={forMentor}
                  hidePreviousTherapyReportsButton={hidePreviousTherapyReportsButton}
                />

                {/* Event type (Assessment, Consultation, Therapy Session, Screening) + preSteps/inSteps */}
                <EventTypeAndStepsStrip
                  bookedSlot={bookedSlot}
                  slotBlocked={isSlotBookedInOtherClinic}
                  forMentor={forMentor}
                  bookedSlotsInDay={bookedSlotsInDay}
                  onGoToEvent={onGoToEvent}
                />

                {/* In Therapy button - past therapy reports */}
                {forMentor && !hidePreviousTherapyReportsButton && bookedSlot?.therapyPatient && (
                  <InTherapyButton
                    bookedSlot={bookedSlot}
                    slotBlocked={isSlotBookedInOtherClinic}
                  />
                )}
                <Box
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative', 
                    flex: '1 1 auto',
                    minWidth: 0, // Allow truncation
                    overflow: 'hidden', // Prevent overflow
                  }}
                >
                  {/* Clinic nickname tag - REMOVED: No need to show clinic name/shortname on the strip card */}

                  {/* Patient information */}
                  <PatientInfo
                    bookedSlot={bookedSlot}
                    apptCalendarSettings={apptCalendarSettings}
                    forMentor={forMentor}
                    styles={styles}
                    clinicAssociationsData={clinicAssociationsData}
                    showClinicIndicator={showClinicIndicator}
                    isBlockedSlot={
                      bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
                      bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
                    }
                    openPaymentLinksDialog={dialogs.paymentLinks.open}
                    isSlotBookedInOtherClinic={isSlotBookedInOtherClinic}
                    clinicAssociations={clinicAssociations}
                  />

                  {/* Steps check and report info - hide report info on narrow screens */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StepsCheckUI 
                      bookedSlot={bookedSlot}
                      onOpenVitalsDialog={() => {
                        setClickedBookedSlotData(bookedSlot)
                        setAddVitalsDialogOpen(true)
                      }}
                    />
                    {includeReportRelatedData && (
                      <Box
                        sx={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          color: styles.fontColor,
                          display: { xs: 'none', md: 'block' }, // Hide on xs/sm, show on md+
                        }}
                      >
                        {(bookedSlot?.reportContentSize !== undefined ||
                          bookedSlot?.reportImagesNumber !== undefined) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {bookedSlot?.reportContentSize !== undefined && (
                              <span>
                                📄 Report Size:{' '}
                                {bookedSlot.reportContentSize > 0
                                  ? `${bookedSlot.reportContentSize} chars`
                                  : 'Empty'}
                              </span>
                            )}
                            {bookedSlot?.reportImagesNumber !== undefined && (
                              <span>🖼️ Attached Images: {bookedSlot.reportImagesNumber || 0}</span>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Action buttons */}
              <ActionButtons
                bookedSlot={bookedSlot}
                forMentor={forMentor}
                minimalUI={minimalUI}
                therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
                handleNewTherapyRequest={handleNewTherapyRequest}
                canPersonPrintPrescription={canPersonPrintPrescription}
                showPrescriptionUploadButton={showPrescriptionUploadButton}
                openReceiptDialog={openReceiptDialog}
                setEventDataForReceiptPrinting={setEventDataForReceiptPrinting}
                openUploadPrescriptionImagesDialog={dialogs.uploadPrescriptionImages.open}
                openPaymentLinksDialog={dialogs.paymentLinks.open}
                openEmrImagesDialog={dialogs.emrImages.open}
                openMenu={openMenu}
                onOpenMainDrawer={onOpenMainDrawer}
              />
            </>
          )}
        </Box>
        )}
      </EDVCSlotColoredItem>

      {/* More options menu */}
      <MoreOptionsForBookedSlotStrip
        handleNewTherapyRequest={handleNewTherapyRequest}
        therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
        bookedSlot={bookedSlot}
        setClickedBookedSlotData={setClickedBookedSlotData}
        setEditPatientDialogOpen={setEditPatientDialogOpen}
        forMentor={forMentor}
        setAddVitalsDialogOpen={handleOpenAddVitals}
        setEditBookingDialogOpen={setEditBookingDialogOpen}
        showViewReportsButtonInActionButtons={showViewReportsButtonInActionButtons}
        refreshBookedEvents={refreshBookedSlots}
        openSendTherapyPopover={openSendTherapyPopover}
        handleOpenJoiningDetails={handleOpenJoiningDetails}
        forceShowJoiningDetailsActionButton={forceShowJoiningDetailsActionButton}
        openBookedEventLogs={dialogs.bookedEventLogs.open}
        openUploadPrescriptionImagesDialog={handleOpenUploadPrescription}
        openSendAssessmentDialog={dialogs.sendAssessment.open}
        openPatientBookingDialog={dialogs.patientBooking.open}
        handleToggleRecording={handleToggleRecording}
        showPrescriptionPdfDownloadButtons={showPrescriptionPdfDownloadButtons}
        openPrescriptionPdfDownloadDialog={prescriptionDialogs.openOptionsDialog}
        openRescheduleDialog={dialogs.reschedule.open}
        hideRescheduleDialog={hideRescheduleDialog}
        openReportViewDrawer={openReportViewDrawer}
        ref={menuRef}
      />

      {/* All dialogs */}
      <BookedSlotDialogs
        dialogs={dialogs}
        bookedSlot={bookedSlot}
        mentorProfileData={mentorProfileData}
        clinicLocationData={clinicLocationData}
        prescriptionGetApiSignature={prescriptionGetApiSignature}
        prescriptionSaveApiSignature={prescriptionSaveApiSignature}
        fileUploadApiSignature={fileUploadApiSignature}
        showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
        sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
        sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
        prescriptionImagesGetApiSignature={prescriptionImagesGetApiSignature}
        prescriptionImagesSaveApiSignature={prescriptionImagesSaveApiSignature}
        refreshBookedSlots={refreshBookedSlots}
        prescriptionDialogs={prescriptionDialogs}
        calculateCreatedTimeFrom={calculateCreatedTimeFrom}
        calculateCreatedTimeTo={calculateCreatedTimeTo}
        showPatientHistoryInUploadPrescriptionWidget={showPatientHistoryInUploadPrescriptionWidget}
        patientHistoryGetApiSignature={patientHistoryGetApiSignature}
        emrImagesGetApiSignature={emrApiData.getApiSignature}
        emrImagesSaveApiSignature={emrApiData.saveApiSignature}
        emrImagesUploadImagesSignature={emrApiData.uploadImagesSignature}
        showSendingLinkButtonsToSupport={showSendingLinkButtonsToSupport}
        emrDialogTitle={emrApiData.dialogTitle}
      />

      {/* Unified Actions Drawer for Mentor Flow */}
      {useUnifiedDrawer && forMentor && (
        <UnifiedActionsDrawer
          open={unifiedDrawerOpen}
          onClose={() => setUnifiedDrawerOpen(false)}
          initialTab={unifiedDrawerInitialTab}
          availableTabs={(() => {
            const tabs = []
            // Joining Details - for online sessions
            const isBookedSlotBlockedForPayment =
              bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
              bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
            if (
              bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE &&
              !isBookedSlotBlockedForPayment
            ) {
              tabs.push('joiningDetails')
            }
            // Upload Prescription - for consultations
            if (bookedSlot?.eventType === EVENT_TYPE_CONSULTATION) {
              tabs.push('uploadPrescription')
            }
            // Add Vitals - always available if not blocked
            if (!isBookedSlotBlockedForPayment) {
              tabs.push('addVitals')
            }
            // Refer Patient - only for doctors
            if (getAllRolesInBoolean().isDoctor) {
              tabs.push('referPatient')
            }
            return tabs
          })()}
          bookedSlot={bookedSlot}
          mentorProfileData={mentorProfileData}
          clinicLocationData={clinicLocationData}
          refreshBookedSlots={refreshBookedSlots}
          apiSignatures={{
            sendSecureSessionLinkToPatientSignature,
            sendPublicSessionLinkToPatientSignature,
            prescriptionGetApiSignature,
            prescriptionSaveApiSignature,
            fileUploadApiSignature,
            prescriptionImagesGetApiSignature,
            prescriptionImagesSaveApiSignature,
            patientHistoryGetApiSignature,
            vitalsFormGetApiSignature,
          }}
          dialogSettings={{
            showSendingLinkButtonsToSupport,
            showPatientHistoryInUploadPrescriptionWidget,
            showGalleryForPrescriptionImages,
            galleryImagesSearch: {
              createdTimeFrom: calculateCreatedTimeFrom(bookedSlot, clinicLocationData),
              createdTimeTo: calculateCreatedTimeTo(bookedSlot, clinicLocationData),
            },
          }}
          forMentor={forMentor}
        />
      )}
    </>
  )
}

export default BookedSlotItemContent
